"use client";

import { useEffect, useState } from "react";
import {
  Megaphone,
  Plus,
  Pencil,
  Trash2,
  Eye,
  Pause,
  Play,
  BarChart3,
  type LucideIcon,
} from "lucide-react";
import {
  PageHeader,
  Card,
  Stat,
  List,
  ListItem,
  SearchField,
  Spinner,
  Empty,
  Dialog,
  ConfirmDialog,
  useToast,
  Button,
} from "@/components/ui";

import { Field, Input, Textarea, Select } from "@/components/ui/ExamplePrimitives";
import { adminApi } from "@/lib/adminApi";
import { cn } from "@/lib/utils";

type AdType = "BANNER" | "SQUARE" | "INTERSTITIAL" | "REWARDED" | "GOOGLE_ADSENSE";
type AdStatus = "DRAFT" | "ACTIVE" | "PAUSED" | "EXPIRED";
type AdCtaType = "LINK" | "SCREEN";

interface Ad {
  id: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  clickUrl: string | null;
  ctaType: AdCtaType | null;
  ctaLabel: string | null;
  screenName: string | null;
  adSenseSlotId: string | null;
  adSenseFormat: string | null;
  type: AdType;
  status: AdStatus;
  placement: string;
  targetRegion: string | null;
  targetGender: string | null;
  targetAgeMin: number | null;
  targetAgeMax: number | null;
  startDate: string | null;
  endDate: string | null;
  impressions: number;
  clicks: number;
  conversions: number;
  dailyLimit: number;
  dailyImpressions: number;
  isPremiumPlacement: boolean;
  priority: number;
  createdAt: string;
}

interface Stats {
  total: number;
  active: number;
  paused: number;
  expired: number;
  draft: number;
  totalImpressions: number;
  totalClicks: number;
  averageCtr: number;
  byType: { type: AdType; count: number }[];
}

interface Analytics {
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number;
  conversionRate: number;
  dailyImpressions: number;
  dailyLimit: number;
  timeline: { date: string; impressions: number; clicks: number; conversions: number }[];
}

const TYPES: AdType[] = ["BANNER", "SQUARE", "INTERSTITIAL", "REWARDED", "GOOGLE_ADSENSE"];
const PLACEMENTS = [
  "home",
  "landing",
  "dashboard_bottom",
  "viewer_intro",
  "viewer_end",
  "pricing_sidebar",
  "profile",
];
const STATUSES: AdStatus[] = ["DRAFT", "ACTIVE", "PAUSED", "EXPIRED"];

const STATUS_BADGE_VARIANT: Record<AdStatus, "success" | "warning" | "neutral" | "info"> = {
  ACTIVE: "success",
  PAUSED: "warning",
  EXPIRED: "neutral",
  DRAFT: "info",
};

interface FormState {
  title: string;
  description: string;
  imageUrl: string;
  clickUrl: string;
  ctaType: AdCtaType;
  ctaLabel: string;
  screenName: string;
  adSenseSlotId: string;
  adSenseFormat: string;
  type: AdType;
  status: AdStatus;
  placement: string;
  targetRegion: string;
  targetGender: string;
  targetAgeMin: string;
  targetAgeMax: string;
  startDate: string;
  endDate: string;
  dailyLimit: string;
  priority: string;
  isPremiumPlacement: boolean;
}

const EMPTY_FORM: FormState = {
  title: "",
  description: "",
  imageUrl: "",
  clickUrl: "",
  ctaType: "LINK",
  ctaLabel: "",
  screenName: "",
  adSenseSlotId: "",
  adSenseFormat: "",
  type: "BANNER",
  status: "DRAFT",
  placement: "home",
  targetRegion: "",
  targetGender: "",
  targetAgeMin: "",
  targetAgeMax: "",
  startDate: "",
  endDate: "",
  dailyLimit: "100",
  priority: "1",
  isPremiumPlacement: false,
};

function adToForm(ad: Ad): FormState {
  return {
    title: ad.title,
    description: ad.description || "",
    imageUrl: ad.imageUrl || "",
    clickUrl: ad.clickUrl || "",
    ctaType: (ad.ctaType as AdCtaType) || "LINK",
    ctaLabel: ad.ctaLabel || "",
    screenName: ad.screenName || "",
    adSenseSlotId: ad.adSenseSlotId || "",
    adSenseFormat: ad.adSenseFormat || "",
    type: ad.type,
    status: ad.status,
    placement: ad.placement,
    targetRegion: ad.targetRegion || "",
    targetGender: ad.targetGender || "",
    targetAgeMin: ad.targetAgeMin?.toString() || "",
    targetAgeMax: ad.targetAgeMax?.toString() || "",
    startDate: ad.startDate ? ad.startDate.slice(0, 16) : "",
    endDate: ad.endDate ? ad.endDate.slice(0, 16) : "",
    dailyLimit: ad.dailyLimit.toString(),
    priority: ad.priority.toString(),
    isPremiumPlacement: ad.isPremiumPlacement,
  };
}

function formToPayload(form: FormState) {
  return {
    title: form.title.trim(),
    description: form.description.trim() || null,
    imageUrl: form.imageUrl.trim() || null,
    clickUrl: form.clickUrl.trim() || null,
    ctaType: form.ctaType || null,
    ctaLabel: form.ctaLabel.trim() || null,
    screenName: form.screenName.trim() || null,
    adSenseSlotId: form.adSenseSlotId.trim() || null,
    adSenseFormat: form.adSenseFormat.trim() || null,
    type: form.type,
    status: form.status,
    placement: form.placement,
    targetRegion: form.targetRegion.trim() || null,
    targetGender: form.targetGender.trim() || null,
    targetAgeMin: form.targetAgeMin ? parseInt(form.targetAgeMin) : null,
    targetAgeMax: form.targetAgeMax ? parseInt(form.targetAgeMax) : null,
    startDate: form.startDate ? new Date(form.startDate).toISOString() : null,
    endDate: form.endDate ? new Date(form.endDate).toISOString() : null,
    dailyLimit: parseInt(form.dailyLimit) || 100,
    priority: parseInt(form.priority) || 1,
    isPremiumPlacement: form.isPremiumPlacement,
  };
}

export default function AdsPage() {
  const [ads, setAds] = useState<Ad[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<"" | AdStatus>("");
  const [filterType, setFilterType] = useState<"" | AdType>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingAd, setEditingAd] = useState<Ad | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [analyticsFor, setAnalyticsFor] = useState<Ad | null>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);

  const { showToast } = useToast();

  const fetchAds = async (pageNum = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pageNum.toString(), limit: "20" });
      if (search) params.set("search", search);
      if (filterStatus) params.set("status", filterStatus);
      if (filterType) params.set("type", filterType);
      const res = await adminApi.get<{ success: boolean; ads: Ad[]; total: number; pages: number }>(
        `/admin/ads?${params}`,
      );
      if (res.success) {
        setAds(res.ads);
        setTotal(res.total);
        setTotalPages(res.pages);
        setPage(pageNum);
      }
    } catch (e: any) {
      showToast({ title: "Failed to load ads", description: e?.message, variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await adminApi.get<{ success: boolean; stats: Stats }>("/admin/ads/stats/summary");
      if (res.success) setStats(res.stats);
    } catch {
      // non-fatal
    }
  };

  useEffect(() => {
    fetchAds(1);
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditingAd(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (ad: Ad) => {
    setEditingAd(ad);
    setForm(adToForm(ad));
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      showToast({ title: "Title is required", variant: "error" });
      return;
    }
    if (!form.placement) {
      showToast({ title: "Placement is required", variant: "error" });
      return;
    }
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (editingAd) {
        await adminApi.put(`/admin/ads/${editingAd.id}`, payload);
      } else {
        await adminApi.post("/admin/ads", payload);
      }
      showToast({ title: editingAd ? "Ad updated" : "Ad created", variant: "success" });
      setShowForm(false);
      fetchAds(page);
      fetchStats();
    } catch (e: any) {
      showToast({ title: "Failed to save ad", description: e?.message, variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await adminApi.delete(`/admin/ads/${confirmDeleteId}`);
      showToast({ title: "Ad deleted", variant: "success" });
      setConfirmDeleteId(null);
      fetchAds(page);
      fetchStats();
    } catch (e: any) {
      showToast({ title: "Failed to delete ad", description: e?.message, variant: "error" });
    }
  };

  const toggleStatus = async (ad: Ad) => {
    setTogglingId(ad.id);
    try {
      if (ad.status === "ACTIVE") {
        await adminApi.post(`/admin/ads/${ad.id}/pause`, {});
        showToast({ title: "Ad paused", variant: "info" });
      } else {
        await adminApi.post(`/admin/ads/${ad.id}/publish`, {});
        showToast({ title: "Ad activated", variant: "success" });
      }
      fetchAds(page);
      fetchStats();
    } catch (e: any) {
      showToast({ title: "Failed to update status", description: e?.message, variant: "error" });
    } finally {
      setTogglingId(null);
    }
  };

  const openAnalytics = async (ad: Ad) => {
    setAnalyticsFor(ad);
    setAnalytics(null);
    try {
      const res = await adminApi.get<{ success: boolean; analytics: Analytics }>(
        `/admin/ads/${ad.id}/analytics`,
      );
      if (res.success) setAnalytics(res.analytics);
    } catch (e: any) {
      showToast({ title: "Failed to load analytics", description: e?.message, variant: "error" });
    }
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") fetchAds(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchAds(newPage);
  };

  return (
    <div>
      <PageHeader
        eyebrow="Monetisation"
        title="Ads"
        subtitle={
          stats
            ? `${stats.total} ads · ${stats.totalImpressions.toLocaleString()} lifetime impressions · ${stats.averageCtr}% avg CTR`
            : `${total} ads`
        }
        actions={
          <Button variant="primary" size="md" onClick={openCreate}>
            <Plus className="h-3.5 w-3.5" />
            New Ad
          </Button>
        }
      />

      {stats && (
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="Total Ads" value={stats.total.toLocaleString()} tint="rose" icon={<Megaphone className="h-4 w-4" />} />
          <Stat label="Active" value={stats.active.toLocaleString()} tint="emerald" icon={<Play className="h-4 w-4" />} />
          <Stat label="Impressions" value={stats.totalImpressions.toLocaleString()} tint="blue" icon={<Eye className="h-4 w-4" />} />
          <Stat label="Avg CTR" value={`${stats.averageCtr}%`} tint="purple" icon={<BarChart3 className="h-4 w-4" />} />
        </div>
      )}

      {/* Toolbar */}
      <Card className="!p-3 mt-4">
        <div className="flex flex-wrap items-center gap-3">
          <SearchField
            value={search}
            onChange={(v) => setSearch(v)}
            placeholder="Search by title or description…"
            className="min-w-[220px] flex-1"
          />
          <Field label="">
            <Select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value as "" | AdStatus);
                fetchAds(1);
              }}
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </Field>
          <Field label="">
            <Select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value as "" | AdType);
                fetchAds(1);
              }}
            >
              <option value="">All types</option>
              {TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
          </Field>
          <div className="ml-auto text-[12px] text-dim">
            {total.toLocaleString()} total
          </div>
        </div>
        <input
          type="text"
          aria-hidden
          value={search}
          onChange={() => undefined}
          onKeyDown={handleSearchSubmit}
          className="sr-only"
        />
      </Card>

      {/* Ads list */}
      <Card className="!p-0 mt-4 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner className="!h-6 !w-6" />
          </div>
        ) : ads.length === 0 ? (
          <Empty
            icon={<Megaphone className="h-5 w-5" />}
            title="No ads found"
            message="Try a different search or filter, or create a new ad."
            action={
              <Button variant="primary" size="sm" onClick={openCreate}>
                <Plus className="h-3.5 w-3.5" />
                New Ad
              </Button>
            }
          />
        ) : (
          <List className="!rounded-none">
            {ads.map((ad, i) => (
              <AdRow
                key={ad.id}
                ad={ad}
                noSeparator={i === 0}
                toggling={togglingId === ad.id}
                onView={() => openAnalytics(ad)}
                onToggle={() => toggleStatus(ad)}
                onEdit={() => openEdit(ad)}
                onDelete={() => setConfirmDeleteId(ad.id)}
              />
            ))}
          </List>
        )}
      </Card>

      {/* Pagination */}
      {ads.length > 0 && (
        <div className="mt-4 flex items-center justify-between text-[12px] text-dim">
          <span>
            Page {page} of {totalPages} — {total} ads
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editingAd ? "Edit Ad" : "New Ad"}
        width={680}
      >
        <div className="space-y-4">
          <Field label="Title" required>
            <Input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </Field>
          <Field label="Description">
            <Textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={2}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type" required>
              <Select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as AdType }))}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </Select>
            </Field>
            <Field label="Placement" required>
              <Select value={form.placement} onChange={(e) => setForm((f) => ({ ...f, placement: e.target.value }))}>
                {PLACEMENTS.map((p) => <option key={p} value={p}>{p}</option>)}
              </Select>
            </Field>
          </div>

          {form.type === "GOOGLE_ADSENSE" ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="AdSense Slot ID" required>
                <Input
                  value={form.adSenseSlotId}
                  onChange={(e) => setForm((f) => ({ ...f, adSenseSlotId: e.target.value }))}
                />
              </Field>
              <Field label="AdSense Format">
                <Input
                  value={form.adSenseFormat}
                  onChange={(e) => setForm((f) => ({ ...f, adSenseFormat: e.target.value }))}
                  placeholder="auto"
                />
              </Field>
            </div>
          ) : (
            <Field label="Image URL">
              <Input
                type="url"
                value={form.imageUrl}
                onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
              />
            </Field>
          )}

          <div className="grid grid-cols-3 gap-3">
            <Field label="CTA Type">
              <Select value={form.ctaType} onChange={(e) => setForm((f) => ({ ...f, ctaType: e.target.value as AdCtaType }))}>
                <option value="LINK">LINK</option>
                <option value="SCREEN">SCREEN</option>
              </Select>
            </Field>
            <div className="col-span-2">
              <Field label={form.ctaType === "SCREEN" ? "Screen Name" : "Click URL"}>
                {form.ctaType === "SCREEN" ? (
                  <Input
                    value={form.screenName}
                    onChange={(e) => setForm((f) => ({ ...f, screenName: e.target.value }))}
                    placeholder="/dashboard"
                  />
                ) : (
                  <Input
                    type="url"
                    value={form.clickUrl}
                    onChange={(e) => setForm((f) => ({ ...f, clickUrl: e.target.value }))}
                  />
                )}
              </Field>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="CTA Label">
              <Input
                value={form.ctaLabel}
                onChange={(e) => setForm((f) => ({ ...f, ctaLabel: e.target.value }))}
                placeholder="Learn more"
              />
            </Field>
            <Field label="Status">
              <Select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as AdStatus }))}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Start Date">
              <Input
                type="datetime-local"
                value={form.startDate}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
              />
            </Field>
            <Field label="End Date">
              <Input
                type="datetime-local"
                value={form.endDate}
                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
              />
            </Field>
          </div>

          <details className="rounded-xl border border-line bg-abyss/30 p-3">
            <summary className="cursor-pointer text-[13px] font-medium text-cream">Advanced (targeting & limits)</summary>
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Region">
                  <Input
                    value={form.targetRegion}
                    onChange={(e) => setForm((f) => ({ ...f, targetRegion: e.target.value }))}
                    placeholder="e.g. US"
                  />
                </Field>
                <Field label="Gender">
                  <Select value={form.targetGender} onChange={(e) => setForm((f) => ({ ...f, targetGender: e.target.value }))}>
                    <option value="">Any</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="other">Other</option>
                  </Select>
                </Field>
                <Field label="Priority">
                  <Input
                    type="number"
                    value={form.priority}
                    onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Age Min">
                  <Input
                    type="number"
                    value={form.targetAgeMin}
                    onChange={(e) => setForm((f) => ({ ...f, targetAgeMin: e.target.value }))}
                  />
                </Field>
                <Field label="Age Max">
                  <Input
                    type="number"
                    value={form.targetAgeMax}
                    onChange={(e) => setForm((f) => ({ ...f, targetAgeMax: e.target.value }))}
                  />
                </Field>
                <Field label="Daily Limit">
                  <Input
                    type="number"
                    value={form.dailyLimit}
                    onChange={(e) => setForm((f) => ({ ...f, dailyLimit: e.target.value }))}
                  />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-[12.5px] text-cream/80">
                <input
                  type="checkbox"
                  checked={form.isPremiumPlacement}
                  onChange={(e) => setForm((f) => ({ ...f, isPremiumPlacement: e.target.checked }))}
                  className="h-4 w-4 rounded text-rose focus:ring-rose"
                />
                <span>Premium placement (only show to PRO users)</span>
              </label>
            </div>
          </details>
        </div>

        <div className="dialog-actions">
          <Button variant="secondary" size="md" onClick={() => setShowForm(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner className="!h-3.5 !w-3.5" /> : null}
            {editingAd ? "Update" : "Create"}
          </Button>
        </div>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete ad?"
        description="This permanently deletes the ad and all its analytics events. This cannot be undone."
        confirmLabel="Delete ad"
        variant="destructive"
      />

      {/* Analytics dialog */}
      <Dialog
        open={!!analyticsFor}
        onClose={() => {
          setAnalyticsFor(null);
          setAnalytics(null);
        }}
        title={analyticsFor ? `Analytics — ${analyticsFor.title}` : "Analytics"}
        width={560}
      >
        {analyticsFor && (
          <>
            {!analytics ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="!h-6 !w-6" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <SmallStat label="Impressions" value={analytics.impressions.toLocaleString()} />
                  <SmallStat label="Clicks" value={analytics.clicks.toLocaleString()} />
                  <SmallStat label="CTR" value={`${analytics.ctr}%`} />
                  <SmallStat label="Conversions" value={analytics.conversions.toLocaleString()} />
                  <SmallStat label="Conv. Rate" value={`${analytics.conversionRate}%`} />
                  <SmallStat label="Today" value={`${analytics.dailyImpressions}/${analytics.dailyLimit}`} />
                </div>
                {analytics.timeline.length > 0 && (
                  <div>
                    <p className="mb-2 text-[13px] font-medium text-cream">Last 14 days</p>
                    <div className="max-h-64 overflow-y-auto rounded-xl border border-line">
                      <table className="w-full text-[12.5px]">
                        <thead className="sticky top-0 bg-abyss/80 backdrop-blur">
                          <tr>
                            <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-dim">Date</th>
                            <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-dim">Impr.</th>
                            <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-dim">Clicks</th>
                            <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-dim">Conv.</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-line/40">
                          {analytics.timeline.map((t) => (
                            <tr key={t.date}>
                              <td className="px-3 py-1.5">{t.date}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums">{t.impressions}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums">{t.clicks}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums">{t.conversions}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="dialog-actions">
              <Button variant="secondary" size="md" onClick={() => {
                setAnalyticsFor(null);
                setAnalytics(null);
              }}>
                Close
              </Button>
            </div>
          </>
        )}
      </Dialog>
    </div>
  );
}

/* ─── Row renderer ─────────────────────────────────────────────────────── */

function AdRow({
  ad, noSeparator, toggling, onView, onToggle, onEdit, onDelete,
}: {
  ad: Ad;
  noSeparator: boolean;
  toggling: boolean;
  onView: () => void;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const statusVariant = STATUS_BADGE_VARIANT[ad.status] ?? "neutral";
  const LeadingIcon: LucideIcon = Megaphone;
  const ctr = ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : "0.00";

  return (
    <ListItem
      noSeparator={noSeparator}
      leading={ad.imageUrl ? (
        <img src={ad.imageUrl} alt="" className="h-full w-full rounded-xl object-cover" />
      ) : (
        <LeadingIcon className="h-4 w-4" />
      )}
      leadingTint="pink"
      title={
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{ad.title}</span>
          <StatusBadge variant={statusVariant}>{ad.status}</StatusBadge>
        </span>
      }
      subtitle={
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>{ad.type}</span>
          <span>· {ad.placement}</span>
          {ad.description && <span className="line-clamp-1 max-w-xs">{ad.description}</span>}
        </span>
      }
      trailing={
        <div className="flex items-center gap-2 text-[12px] tabular-nums">
          <div className="hidden flex-col items-end sm:flex">
            <span className="text-cream/80">{ad.impressions.toLocaleString()} impr</span>
            <span className="text-dim">{ctr}% CTR</span>
          </div>
          <Button variant="outline" size="sm" iconOnly onClick={onView} title="Analytics">
            <BarChart3 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant={ad.status === "ACTIVE" ? "outline" : "success"}
            size="sm"
            iconOnly
            disabled={toggling}
            onClick={onToggle}
            title={ad.status === "ACTIVE" ? "Pause" : "Activate"}
          >
            {toggling ? <Spinner className="!h-3.5 !w-3.5" /> : ad.status === "ACTIVE" ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="outline" size="sm" iconOnly onClick={onEdit} title="Edit">
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" iconOnly onClick={onDelete} title="Delete">
            <Trash2 className="h-3.5 w-3.5 text-red-400" />
          </Button>
        </div>
      }
    />
  );
}

function SmallStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-line bg-abyss/40 p-3">
      <p className="text-2xl font-bold">{value}</p>
      <p className="mt-1 text-[10px] uppercase tracking-wider text-dim">{label}</p>
    </div>
  );
}

function StatusBadge({
  variant, children,
}: {
  variant: "success" | "warning" | "neutral" | "info";
  children: React.ReactNode;
}) {
  const tintMap: Record<string, string> = {
    success: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30",
    warning: "bg-amber-500/15 text-amber-200 ring-amber-400/30",
    info:    "bg-blue-500/15 text-blue-300 ring-blue-400/30",
    neutral: "bg-white/8 text-white/70 ring-white/15",
  };
  return (
    <span className={cn(
      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ring-inset",
      tintMap[variant],
    )}>
      {children}
    </span>
  );
}